import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { AdminPanelSettingsOutlined, ExpandMore, RefreshOutlined } from '@mui/icons-material';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import {
  isLikelyObjectId,
  useEntitlementsSupportOverview,
} from '@/state/queries/entitlements/use-entitlements-support-overview';
import {
  useAdminAdjustQuotaMutation,
  useAdminExpireEntitlementMutation,
  useAdminGrantEntitlementMutation,
  useAdminRevokeEntitlementMutation,
} from '@/state/mutations/entitlements-admin/use-entitlement-admin-mutations';

const SCOPE_TYPES = ['Event', 'Organization', 'Flow', 'Venue'] as const;

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(n);
}

function formatWhen(s: string | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString();
}

export default function EntitlementsAdminPage() {
  const [draftScopeType, setDraftScopeType] = useState<string>('Event');
  const [draftScopeId, setDraftScopeId] = useState('');
  const [lookup, setLookup] = useState<{ scopeType: string; scopeId: string } | null>(null);

  const overviewQuery = useEntitlementsSupportOverview(lookup?.scopeType, lookup?.scopeId, {
    enabled: !!(lookup && isLikelyObjectId(lookup.scopeId)),
  });

  const grantMut = useAdminGrantEntitlementMutation();
  const revokeMut = useAdminRevokeEntitlementMutation();
  const expireMut = useAdminExpireEntitlementMutation();
  const adjustMut = useAdminAdjustQuotaMutation();

  const [grantKind, setGrantKind] = useState<'QUOTA' | 'FEATURE'>('QUOTA');
  const [grantQuotaKey, setGrantQuotaKey] = useState('');
  const [grantQuantity, setGrantQuantity] = useState('1');
  const [grantFeatureKey, setGrantFeatureKey] = useState('');
  const [grantIdempotencyKey, setGrantIdempotencyKey] = useState('');

  const [entitlementIdAction, setEntitlementIdAction] = useState('');
  const [adjustQuotaKey, setAdjustQuotaKey] = useState('');
  const [adjustDelta, setAdjustDelta] = useState('');

  const activeScope = lookup;

  const grantDisabled = useMemo(() => {
    if (!activeScope) return true;
    if (grantKind === 'QUOTA') {
      return !grantQuotaKey.trim() || !grantQuantity.trim() || Number.parseInt(grantQuantity, 10) <= 0;
    }
    return !grantFeatureKey.trim();
  }, [activeScope, grantKind, grantQuotaKey, grantQuantity, grantFeatureKey]);

  const handleLoad = () => {
    const sid = draftScopeId.trim();
    if (!isLikelyObjectId(sid)) {
      window.alert('Scope id must be a 24-character hex Mongo ObjectId. Do not search by name or title.');
      return;
    }
    setLookup({ scopeType: draftScopeType.trim(), scopeId: sid });
  };

  const handleGrant = async () => {
    if (!activeScope) return;
    try {
      const body =
        grantKind === 'QUOTA'
          ? {
              kind: 'QUOTA' as const,
              scopeType: activeScope.scopeType,
              scopeId: activeScope.scopeId,
              quotaKey: grantQuotaKey.trim(),
              quantity: Number.parseInt(grantQuantity, 10),
              ...(grantIdempotencyKey.trim() ? { idempotencyKey: grantIdempotencyKey.trim() } : {}),
            }
          : {
              kind: 'FEATURE' as const,
              scopeType: activeScope.scopeType,
              scopeId: activeScope.scopeId,
              featureKey: grantFeatureKey.trim(),
              ...(grantIdempotencyKey.trim() ? { idempotencyKey: grantIdempotencyKey.trim() } : {}),
            };
      const res = await grantMut.mutateAsync(body);
      window.alert(`Granted. Entitlement id: ${res.id}`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Grant failed');
    }
  };

  const handleRevoke = async () => {
    const id = entitlementIdAction.trim();
    if (!isLikelyObjectId(id)) {
      window.alert('entitlementId must be a valid ObjectId');
      return;
    }
    if (!window.confirm('Revoke this entitlement?')) return;
    try {
      await revokeMut.mutateAsync({ entitlementId: id });
      await overviewQuery.refetch();
      window.alert('Revoked.');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Revoke failed');
    }
  };

  const handleExpire = async () => {
    const id = entitlementIdAction.trim();
    if (!isLikelyObjectId(id)) {
      window.alert('entitlementId must be a valid ObjectId');
      return;
    }
    if (!window.confirm('Expire this entitlement now?')) return;
    try {
      await expireMut.mutateAsync({ entitlementId: id });
      await overviewQuery.refetch();
      window.alert('Expired.');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Expire failed');
    }
  };

  const handleAdjust = async () => {
    if (!activeScope) return;
    const delta = Number.parseInt(adjustDelta, 10);
    if (!adjustQuotaKey.trim() || Number.isNaN(delta) || delta === 0) {
      window.alert('quotaKey and non-zero integer delta are required');
      return;
    }
    try {
      await adjustMut.mutateAsync({
        scopeType: activeScope.scopeType,
        scopeId: activeScope.scopeId,
        quotaKey: adjustQuotaKey.trim(),
        delta,
      });
      await overviewQuery.refetch();
      window.alert('Adjusted.');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Adjust failed');
    }
  };

  const data = overviewQuery.data;

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <AdminPanelSettingsOutlined sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Entitlements & billing (support)
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Refresh overview"
                  onClick={() => overviewQuery.refetch()}
                  disabled={!lookup || overviewQuery.isFetching}
                >
                  {overviewQuery.isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
                </IconButton>
              </Box>
            }
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            Lookup is <strong>by scope id only</strong> (no event titles or names). The API requires Oso{' '}
            <code>read</code> on that scope for everyone, including users with the admin role. Responses omit titles,
            addresses, user profiles, and raw payment provider ids. Orders for an <strong>Organization</strong>{' '}
            aggregate events under that org without listing event metadata.
          </Alert>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="scope-type-label">Scope type</InputLabel>
                  <Select
                    labelId="scope-type-label"
                    label="Scope type"
                    value={draftScopeType}
                    onChange={(e) => setDraftScopeType(e.target.value)}
                  >
                    {SCOPE_TYPES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Scope id (ObjectId)"
                  value={draftScopeId}
                  onChange={(e) => setDraftScopeId(e.target.value)}
                  placeholder="507f1f77bcf86cd799439011"
                  sx={{ flex: 1, minWidth: 220 }}
                />
                <Button variant="contained" onClick={handleLoad}>
                  Load overview
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {overviewQuery.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {overviewQuery.error instanceof Error ? overviewQuery.error.message : 'Failed to load overview'}
            </Alert>
          )}

          {lookup && overviewQuery.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {data && (
            <Stack spacing={3} sx={{ mt: 2, pb: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Loaded scope: {data.scopeType} / {data.scopeId}
              </Typography>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Quota balances
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>quotaKey</TableCell>
                        <TableCell align="right">available</TableCell>
                        <TableCell align="right">granted</TableCell>
                        <TableCell align="right">consumed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.balances.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              No balance rows
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.balances.map((b) => (
                          <TableRow key={b.quotaKey}>
                            <TableCell>{b.quotaKey}</TableCell>
                            <TableCell align="right">{b.available}</TableCell>
                            <TableCell align="right">{b.totalGranted}</TableCell>
                            <TableCell align="right">{b.totalConsumed}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Entitlements
                </Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>id</TableCell>
                        <TableCell>kind</TableCell>
                        <TableCell>keys</TableCell>
                        <TableCell>source</TableCell>
                        <TableCell>validUntil</TableCell>
                        <TableCell>state</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.entitlements.map((e) => (
                        <TableRow key={e.id}>
                          <TableCell
                            sx={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 120, wordBreak: 'break-all' }}
                          >
                            {e.id}
                          </TableCell>
                          <TableCell>{e.kind}</TableCell>
                          <TableCell>
                            {[e.quotaKey, e.featureKey, e.packageKey].filter(Boolean).join(' · ') || '—'}
                          </TableCell>
                          <TableCell>
                            {e.sourceType}
                            {e.sourceId ? ` (${e.sourceId})` : ''}
                          </TableCell>
                          <TableCell>{formatWhen(e.validUntil)}</TableCell>
                          <TableCell>{e.revokedAt ? 'revoked' : e.expiredAt ? 'expired' : 'active'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Ledger (recent)
                </Typography>
                <TableContainer sx={{ maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>when</TableCell>
                        <TableCell>quotaKey</TableCell>
                        <TableCell align="right">delta</TableCell>
                        <TableCell>reason</TableCell>
                        <TableCell>ref</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.ledgerEntries.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{formatWhen(row.createdAt)}</TableCell>
                          <TableCell>{row.quotaKey}</TableCell>
                          <TableCell align="right">{row.delta}</TableCell>
                          <TableCell>{row.reason}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>
                            {row.referenceType ?? '—'}
                            {row.referenceId ? ` ${row.referenceId}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Package assignments (active on this target)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>id</TableCell>
                        <TableCell>packageKey</TableCell>
                        <TableCell>packageEntitlementId</TableCell>
                        <TableCell>target</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.packageAssignments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              None
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.packageAssignments.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.id}</TableCell>
                            <TableCell>{p.packageKey}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {p.packageEntitlementId}
                            </TableCell>
                            <TableCell>
                              {p.targetScopeType} {p.targetScopeId}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Orders (Event scope, or all events under Organization)
                </Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>order id</TableCell>
                        <TableCell>entity</TableCell>
                        <TableCell>status</TableCell>
                        <TableCell align="right">total</TableCell>
                        <TableCell>lines (product ids)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.orders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary">
                              No orders for this scope type (Flow/Venue have no event orders here)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.orders.map((o) => (
                          <TableRow key={o.id}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{o.id}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {o.entityType} {o.entityId}
                            </TableCell>
                            <TableCell>{o.status}</TableCell>
                            <TableCell align="right">{formatMoney(o.totalPriceWithVat)}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>
                              {o.lineItems.map((li) => `${li.productId}×${li.quantity}`).join(', ') || '—'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Transactions (linked to those orders)
                </Typography>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>id</TableCell>
                        <TableCell>when</TableCell>
                        <TableCell>payment ref?</TableCell>
                        <TableCell>lines</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              None
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.transactions.map((t) => (
                          <TableRow key={t.id}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{t.id}</TableCell>
                            <TableCell>{formatWhen(t.createdAt)}</TableCell>
                            <TableCell>{t.hasExternalPaymentReference ? 'yes' : 'no'}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>
                              {t.lines
                                .map((l) =>
                                  l.lineKind === 'order'
                                    ? `order:${l.orderId}`
                                    : l.lineKind === 'subscription'
                                      ? `plan:${l.subscriptionPlanId}`
                                      : 'other',
                                )
                                .join('; ')}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 600 }}>Admin mutations (grant / revoke / expire / adjust)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      These calls require the <strong>admin</strong> role on the API. They use the loaded scope for
                      grant and adjust. Revoke and expire use an entitlement id from the table above.
                    </Typography>
                    <Typography variant="subtitle2">Grant</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Kind</InputLabel>
                        <Select
                          label="Kind"
                          value={grantKind}
                          onChange={(e) => setGrantKind(e.target.value as 'QUOTA' | 'FEATURE')}
                        >
                          <MenuItem value="QUOTA">QUOTA</MenuItem>
                          <MenuItem value="FEATURE">FEATURE</MenuItem>
                        </Select>
                      </FormControl>
                      {grantKind === 'QUOTA' ? (
                        <>
                          <TextField
                            size="small"
                            label="quotaKey"
                            value={grantQuotaKey}
                            onChange={(e) => setGrantQuotaKey(e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="quantity"
                            value={grantQuantity}
                            onChange={(e) => setGrantQuantity(e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </>
                      ) : (
                        <TextField
                          size="small"
                          label="featureKey"
                          value={grantFeatureKey}
                          onChange={(e) => setGrantFeatureKey(e.target.value)}
                          sx={{ flex: 1 }}
                        />
                      )}
                      <TextField
                        size="small"
                        label="idempotencyKey (optional)"
                        value={grantIdempotencyKey}
                        onChange={(e) => setGrantIdempotencyKey(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Button variant="outlined" onClick={handleGrant} disabled={grantDisabled || grantMut.isPending}>
                        Grant
                      </Button>
                    </Stack>
                    <Typography variant="subtitle2">Revoke / expire by entitlement id</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                      <TextField
                        size="small"
                        label="entitlementId"
                        value={entitlementIdAction}
                        onChange={(e) => setEntitlementIdAction(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Button variant="outlined" color="warning" onClick={handleRevoke} disabled={revokeMut.isPending}>
                        Revoke
                      </Button>
                      <Button variant="outlined" color="error" onClick={handleExpire} disabled={expireMut.isPending}>
                        Expire now
                      </Button>
                    </Stack>
                    <Typography variant="subtitle2">Adjust quota (ledger + projection)</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                      <TextField
                        size="small"
                        label="quotaKey"
                        value={adjustQuotaKey}
                        onChange={(e) => setAdjustQuotaKey(e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="delta (integer, negative allowed)"
                        value={adjustDelta}
                        onChange={(e) => setAdjustDelta(e.target.value)}
                        sx={{ width: 220 }}
                      />
                      <Button variant="outlined" onClick={handleAdjust} disabled={!activeScope || adjustMut.isPending}>
                        Adjust
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
        </Box>
      }
    />
  );
}
